/**
 * Checks if a value is falsy but excludes 0 as falsy
 * Useful for cases where 0 is a valid value but other falsy values should be treated as "not set"
 *
 * @param value - The value to check
 * @returns true if the value is falsy but not 0
 *
 * @example
 * isFalsyExceptZero(0) // false - 0 is not considered falsy
 * isFalsyExceptZero(null) // true
 * isFalsyExceptZero(undefined) // true
 * isFalsyExceptZero('') // true
 * isFalsyExceptZero(false) // true
 * isFalsyExceptZero(1) // false
 * isFalsyExceptZero('hello') // false
 */
export function isFalsyExceptZero(value: unknown): boolean {
  return !value && value !== 0;
}

/**
 * Checks if a value is truthy or 0
 * Useful for cases where 0 is a valid value
 *
 * @param value - The value to check
 * @returns true if the value is truthy or 0
 *
 * @example
 * isTruthyOrZero(0) // true - 0 is considered valid
 * isTruthyOrZero(null) // false
 * isTruthyOrZero(undefined) // false
 * isTruthyOrZero('') // false
 * isTruthyOrZero(false) // false
 * isTruthyOrZero(1) // true
 * isTruthyOrZero('hello') // true
 */
export function isTruthyOrZero(value: unknown): boolean {
  return Boolean(value) || value === 0;
}

/**
 * Checks if a value is defined (not null or undefined) but allows falsy values like 0, false, ''
 *
 * @param value - The value to check
 * @returns true if the value is not null or undefined
 *
 * @example
 * isDefined(0) // true
 * isDefined(false) // true
 * isDefined('') // true
 * isDefined(null) // false
 * isDefined(undefined) // false
 */
export function isDefined<T>(value: T | null | undefined): value is T {
  return value !== null && value !== undefined;
}
